import { useEffect, type ChangeEvent } from 'react';
import { useForm, type ControllerRenderProps } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@predoc/cow';
import { toast } from 'sonner';

import { Footer } from './Footer';
import { AUTH_ASSETS, AUTH_PATHS } from '../constants';
import { mfaPhoneSetupSchema, type MfaPhoneSetupFormData } from '../types';
import {
  formatUsPhoneNumberWithCountryCode,
  normalizePhoneNumberForCognito,
  parsePhoneNumber,
  US_PHONE_NUMBER_DIGIT_LIMIT,
  US_PHONE_NUMBER_WITH_COUNTRY_CODE_DISPLAY_LENGTH,
} from '@/utils';
import { useSetUpSmsMfaPhone } from '../hooks/authApi';
import { useMfa } from '../MfaContext';
import { useAuth } from '@/routes/AuthContext';
import { usePageTitle } from '@/hooks';
import { useMfaSetupGuard } from '../hooks/useMfaSetupGuard';

export const MfaSetupPhone = () => {
  usePageTitle('Set up multi-factor authentication');
  const navigate = useNavigate();
  const { handleSignOut, user } = useAuth();
  const {
    organizationMfaEnabled,
    setAuthEmail,
    setOrganizationMfaEnabled,
    setMfaVerificationMode,
  } = useMfa();
  const { data: mfaGuard, isLoading: isMfaGuardLoading } = useMfaSetupGuard(
    user?.email,
  );
  const { mutateAsync: updatePhoneNumber, isPending: isUpdatingPhone } =
    useSetUpSmsMfaPhone();

  const form = useForm<MfaPhoneSetupFormData>({
    resolver: zodResolver(mfaPhoneSetupSchema),
    mode: 'onSubmit',
    defaultValues: {
      phoneNumber: '',
    },
  });

  const phoneNumber = form.watch('phoneNumber');
  const isPhoneNumberValid =
    parsePhoneNumber(phoneNumber || '').length === US_PHONE_NUMBER_DIGIT_LIMIT;
  const showPhoneNumberError =
    form.formState.isSubmitted && !!form.formState.errors.phoneNumber;

  useEffect(() => {
    if (isMfaGuardLoading) {
      return;
    }

    const canAccessSetupPage =
      mfaGuard?.setupRequired === true || organizationMfaEnabled;

    if (!canAccessSetupPage) {
      navigate(AUTH_PATHS.LOGIN);
    }
  }, [
    isMfaGuardLoading,
    mfaGuard?.setupRequired,
    navigate,
    organizationMfaEnabled,
  ]);

  const handleSubmit = async (data: MfaPhoneSetupFormData) => {
    const normalizedPhoneNumber = normalizePhoneNumberForCognito(
      data.phoneNumber,
    );

    try {
      await updatePhoneNumber({
        phoneNumber: normalizedPhoneNumber,
      });

      setMfaVerificationMode('phone-setup');
      toast.success('Verification Code Sent.');
      navigate(AUTH_PATHS.MFA_VERIFICATION);
    } catch {
      toast.error('Failed to set up MFA phone number.');
    }
  };

  const handleBack = async () => {
    setMfaVerificationMode(null);
    setAuthEmail(null);
    setOrganizationMfaEnabled(false);
    await handleSignOut();
    navigate(AUTH_PATHS.LOGIN);
  };

  if (isMfaGuardLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center text-lg"
        aria-label="Loading"
        tabIndex={0}
      >
        Loading...
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-gray-50">
      <div className="flex w-full flex-1 items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-[478px] flex-col items-center gap-9">
          <img
            src={AUTH_ASSETS.FULL_LOGO_BLACK}
            alt="Predoc Logo"
            className="h-14"
          />

          <Card className="w-full rounded-xl px-12 py-16 shadow-sm sm:px-16">
            <h1 className="mb-6 text-center text-[30px] leading-9 font-bold text-gray-900">
              Set up multi-factor authentication
            </h1>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-8"
                noValidate
              >
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<
                      MfaPhoneSetupFormData,
                      'phoneNumber'
                    >;
                  }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="ex. +1 (234) 567-8901"
                          autoComplete="tel"
                          maxLength={
                            US_PHONE_NUMBER_WITH_COUNTRY_CODE_DISPLAY_LENGTH
                          }
                          className={
                            showPhoneNumberError ? 'border-destructive' : ''
                          }
                          aria-invalid={showPhoneNumberError}
                          aria-describedby={
                            showPhoneNumberError
                              ? 'phone-number-error phone-number-help'
                              : 'phone-number-help'
                          }
                          value={formatUsPhoneNumberWithCountryCode(
                            field.value || '',
                          )}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            field.onChange(
                              parsePhoneNumber(event.target.value),
                            );
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      {showPhoneNumberError && (
                        <FormMessage
                          id="phone-number-error"
                          className="text-destructive mt-1 text-sm"
                          role="alert"
                        >
                          {form.formState.errors.phoneNumber?.message}
                        </FormMessage>
                      )}
                      <p
                        id="phone-number-help"
                        className="text-muted-foreground mt-2 text-sm"
                      >
                        We&apos;ll send your verification code to this phone
                        number.
                      </p>
                    </FormItem>
                  )}
                />

                <div className="flex flex-col items-center gap-4">
                  <Button
                    type="submit"
                    size="lg"
                    className="h-11 w-full rounded-full"
                    disabled={isUpdatingPhone || !isPhoneNumberValid}
                    loading={isUpdatingPhone}
                  >
                    {isUpdatingPhone ? 'Sending Code...' : 'Next'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-2 rounded-full hover:bg-transparent"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    Back
                  </Button>
                </div>
              </form>
            </Form>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};
